import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, ArrowRight, Loader2, UserCheck, Briefcase, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, STAGES } from "@/lib/api";

const FILTERS = [
  { id: "all", label: "All" }, { id: "you", label: "Waiting for you" }, { id: "ai", label: "AI working" }, { id: "done", label: "Completed" },
];
const bucket = (r) => (r.next_action.startsWith("Done") ? "done" : r.status === "running" ? "ai" : "you");

function Stat({ icon: Icon, label, value, tone, onClick, active, id }) {
  return (
    <button data-testid={id} onClick={onClick} className={`card-surface text-left flex items-center gap-4 transition-colors ${active ? "border-blue-500/60" : "hover:border-slate-600"}`}>
      <div className={`h-10 w-10 rounded-lg grid place-items-center ${tone}`}><Icon size={18} /></div>
      <div><div className="text-2xl font-bold font-display">{value}</div><div className="text-xs text-muted-foreground">{label}</div></div>
    </button>
  );
}

export default function ClientList() {
  const [rows, setRows] = useState(null);
  const [f, setF] = useState("all");
  const [q, setQ] = useState("");
  const nav = useNavigate();
  useEffect(() => {
    const load = () => api.get("/clients").then(({ data }) => setRows(data));
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);
  if (!rows) return null;
  const count = (b) => rows.filter((r) => bucket(r) === b).length;
  const shown = rows.filter((r) => (f === "all" || bucket(r) === f) && (!q || `${r.name} ${r.track_title} ${r.visa_status}`.toLowerCase().includes(q.toLowerCase())));

  return (
    <div className="px-10 py-8 max-w-[1400px]">
      <div className="flex items-end justify-between mb-8">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight font-display">Clients</h1>
          <p className="text-sm text-muted-foreground mt-1">Open a client to pick up exactly where you left off. Each step tells you what to do.</p>
        </div>
        <Button data-testid="new-client-button" className="bg-blue-600 hover:bg-blue-500 text-white h-10 px-5" onClick={() => nav("/clients/new")}>
          <Plus size={16} className="mr-1.5" /> New client
        </Button>
      </div>
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Stat id="stat-all" icon={Briefcase} label="All resume jobs" value={rows.length} tone="bg-slate-800 text-slate-300" active={f === "all"} onClick={() => setF("all")} />
        <Stat id="stat-you" icon={UserCheck} label="Waiting for you" value={count("you")} tone="bg-amber-500/15 text-amber-300" active={f === "you"} onClick={() => setF("you")} />
        <Stat id="stat-ai" icon={Loader2} label="AI working" value={count("ai")} tone="bg-blue-500/15 text-blue-300" active={f === "ai"} onClick={() => setF("ai")} />
        <Stat id="stat-done" icon={CheckCircle2} label="Completed" value={count("done")} tone="bg-emerald-500/15 text-emerald-300" active={f === "done"} onClick={() => setF("done")} />
      </div>
      <div className="flex items-center gap-2 mb-4">
        {FILTERS.map((x) => (
          <button key={x.id} data-testid={`filter-${x.id}`} onClick={() => setF(x.id)}
            className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${f === x.id ? "bg-blue-600/20 border-blue-500/50 text-blue-200" : "border-border text-slate-400 hover:text-slate-200"}`}>{x.label}</button>
        ))}
        <div className="ml-auto relative">
          <Search size={14} className="absolute left-3 top-2.5 text-slate-500" />
          <input data-testid="client-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search clients"
            className="bg-slate-900 border border-border rounded-lg pl-8 pr-3 py-2 text-sm w-72 outline-none focus:border-blue-500" />
        </div>
      </div>
      <div className="rounded-xl border border-border overflow-hidden">
        <table data-testid="client-list-table" className="w-full text-sm">
          <thead className="bg-slate-900/70 text-left text-[11px] uppercase tracking-wider text-muted-foreground">
            <tr><th className="px-5 py-3">Client</th><th className="px-4">Target title</th><th className="px-4 w-64">Progress</th><th className="px-4">Next step</th><th /></tr>
          </thead>
          <tbody>
            {shown.map((r) => {
              const b = bucket(r);
              return (
                <tr key={r.job_id} data-testid={`client-row-${r.job_id}`} onClick={() => nav(`/jobs/${r.job_id}`)} className="border-t border-border hover:bg-slate-900/60 cursor-pointer transition-colors group">
                  <td className="px-5 py-4"><div className="font-semibold">{r.name}</div><div className="text-xs text-muted-foreground">{r.visa_status || "Visa not set"} · {r.location || "Location not set"}</div></td>
                  <td className="px-4">{r.track_title}<div className="text-xs text-muted-foreground">{r.seniority}</div></td>
                  <td className="px-4">
                    <div className="flex items-center gap-2"><div className="flex-1 h-1.5 rounded-full bg-slate-800 overflow-hidden"><div className="h-full bg-gradient-to-r from-blue-500 to-emerald-500" style={{ width: `${(100 * r.approved) / r.total}%` }} /></div>
                      <span className="text-xs font-mono text-slate-400">{r.approved}/{r.total}</span></div>
                    <div className="text-[11px] text-slate-500 mt-1">Step {STAGES.findIndex((s) => s.id === r.current_stage) + 1}: {STAGES.find((s) => s.id === r.current_stage)?.name}</div>
                  </td>
                  <td className="px-4">
                    <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full ${b === "ai" ? "bg-blue-500/10 text-blue-300" : b === "done" ? "bg-emerald-500/10 text-emerald-300" : "bg-amber-500/10 text-amber-300"}`} data-testid={`next-action-${r.job_id}`}>
                      {b === "ai" && <Loader2 size={11} className="animate-spin" />}{r.next_action}
                    </span>
                  </td>
                  <td className="px-4 text-right"><span data-testid={`open-job-${r.job_id}`} className="inline-flex items-center gap-1 text-blue-400 text-xs opacity-60 group-hover:opacity-100">Open <ArrowRight size={12} /></span></td>
                </tr>
              );
            })}
            {!shown.length && <tr><td colSpan={5} className="px-4 py-16 text-center text-muted-foreground" data-testid="client-list-empty">{rows.length ? "No clients match." : "No clients yet. Click \"New client\" to start."}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
